import { useState, useEffect } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, Search, Filter, Heart, Stethoscope, 
  Utensils, Baby, FileText, AlertTriangle, Calendar, 
  TrendingUp, Pill, Syringe, Weight, Thermometer, Activity,
  ClipboardList, BookOpen, Euro, ArrowRight, Users,
  Home, TreePine, Droplets, Sun, Moon, PawPrint
} from 'lucide-react';

// Icônes personnalisées pour lapin et cheval
const Rabbit = PawPrint;
const Horse = PawPrint;

// ========== DONNÉES MÉTIER ÉLEVAGE ==========

// Réglementation sanitaire pour lapins
const REGLEMENTATION_LAPIN = {
  identification: {
    obligatoire: true,
    description: "Tatouage à l'oreille ou puce électronique pour les reproducteurs",
    texte_reference: "Arrêté du 19 janvier 2009"
  },
  vaccinations: [
    { nom: "Myxomatose", frequence: "Annuelle", obligatoire: true, note: "Vaccin Nobivac Myxo-RHD recommandé" },
    { nom: "VHD (Maladie hémorragique virale)", frequence: "Annuelle", obligatoire: true, note: "Variants VHD1 et VHD2" },
    { nom: "Pasteurellose", frequence: "Selon risque", obligatoire: false, note: "Recommandé en élevage intensif" }
  ],
  normes_bien_etre: {
    surface_min_par_animal: "0.2 m² par lapin adulte en cage individuelle",
    temperature: "15-25°C optimal, éviter >30°C",
    eclairage: "14-16h de lumière/jour pour reproduction",
    ventilation: "Renouvellement d'air 3-4 fois/heure"
  },
  registre_obligatoire: [
    "Registre d'élevage (mouvements, naissances, mortalité)",
    "Registre des traitements vétérinaires",
    "Registre des aliments avec traçabilité"
  ]
};

// Réglementation sanitaire pour équidés
const REGLEMENTATION_CHEVAL = {
  identification: {
    obligatoire: true,
    description: "Puce électronique + document d'identification + passeport équin",
    texte_reference: "Règlement UE 2015/262, SIRE obligatoire en France",
    delai: "Poulain identifié avant 12 mois ou avant sortie de l'élevage"
  },
  vaccinations: [
    { nom: "Grippe équine", frequence: "Tous les 6 mois (compétition) / Annuelle (loisir)", obligatoire: true, note: "Obligatoire pour rassemblements" },
    { nom: "Tétanos", frequence: "Rappel tous les 2-3 ans", obligatoire: false, note: "Très recommandé" },
    { nom: "Rhinopneumonie", frequence: "Tous les 6 mois pour juments gestantes", obligatoire: false, note: "Prévention avortements" },
    { nom: "Rage", frequence: "Annuelle si zone à risque", obligatoire: false, note: "Selon département" }
  ],
  vermifugation: {
    frequence: "4 fois/an minimum",
    principes_actifs: ["Ivermectine", "Moxidectine", "Fenbendazole", "Pyrantel"],
    coproscopie: "Recommandée avant traitement pour adapter le protocole"
  },
  soins_obligatoires: [
    { nom: "Dentiste équin", frequence: "Annuelle", note: "Râpage des dents" },
    { nom: "Maréchal-ferrant", frequence: "Toutes les 6-8 semaines", note: "Parage ou ferrage" },
    { nom: "Ostéopathe", frequence: "2 fois/an", note: "Recommandé pour chevaux de sport" }
  ],
  normes_bien_etre: {
    surface_box: "Minimum 9m² pour un cheval, 12m² recommandé",
    surface_paddock: "Minimum 500m² par cheval",
    surface_pre: "0.5 à 1 hectare par cheval selon qualité herbe",
    eau: "30-50 litres/jour par cheval",
    foin: "10-15 kg/jour selon activité"
  }
};

// Alimentation lapins
const ALIMENTATION_LAPIN = {
  base: [
    { aliment: "Foin de prairie", quantite: "À volonté (70% de la ration)", obligatoire: true, note: "Base de l'alimentation, usure des dents" },
    { aliment: "Granulés complets", quantite: "25-30g par kg de poids vif/jour", obligatoire: true, note: "Minimum 18% fibres" },
    { aliment: "Légumes frais", quantite: "50-100g/jour", obligatoire: false, note: "Carotte, céleri, fenouil" },
    { aliment: "Eau fraîche", quantite: "100-300ml/jour", obligatoire: true, note: "Pipette ou abreuvoir propre" }
  ],
  reproduction: [
    { aliment: "Granulés enrichis gestation", quantite: "+20% ration normale", periode: "Gestation/lactation" },
    { aliment: "Luzerne déshydratée", quantite: "Complément", periode: "Lactation" }
  ],
  interdits: ["Pain", "Biscuits", "Chocolat", "Pomme de terre crue", "Oignon", "Ail", "Avocat"]
};

// Alimentation chevaux
const ALIMENTATION_CHEVAL = {
  base: [
    { aliment: "Foin de prairie", quantite: "1.5-2% du poids vif/jour", obligatoire: true, note: "Ex: 7.5-10kg pour cheval de 500kg" },
    { aliment: "Eau", quantite: "30-50 litres/jour", obligatoire: true, note: "Accès permanent recommandé" },
    { aliment: "Pierre à sel", quantite: "À disposition", obligatoire: true, note: "Sel + minéraux" }
  ],
  travail: [
    { aliment: "Avoine", quantite: "1-3 kg/jour selon effort", type_travail: "Travail léger à intense" },
    { aliment: "Orge aplatie", quantite: "1-2 kg/jour", type_travail: "Travail modéré" },
    { aliment: "Granulés sport", quantite: "2-4 kg/jour", type_travail: "Compétition" }
  ],
  complement: [
    { nom: "CMV (Complexe Minéral Vitaminé)", frequence: "Quotidien", note: "Essentiel si peu de pâture" },
    { nom: "Biotine", frequence: "Quotidien", note: "Qualité des sabots" },
    { nom: "Électrolytes", frequence: "Après effort", note: "Récupération" }
  ],
  interdits: ["Gazon tondu fermenté", "Plantes toxiques (if, laurier rose, séneçon)", "Pain moisi", "Pommes en excès"]
};

// Composant principal
export default function ElevagePage() {
  const { activeSociete, tenant } = useWorkspace();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddAnimal, setShowAddAnimal] = useState(false);
  const [selectedEspece, setSelectedEspece] = useState('all');
  
  // États pour les données
  const [animaux, setAnimaux] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [stats, setStats] = useState({
    totalLapins: 0,
    totalChevaux: 0,
    gestations: 0,
    alertesSante: 0
  });
  const [loading, setLoading] = useState(false);

  // Charger les données au montage
  useEffect(() => {
    loadData();
  }, [activeSociete]);

  const loadData = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Societe-Id': activeSociete?.id || ''
      };
      
      // Charger les animaux
      const response = await fetch(`${API_URL}/api/elevage/animaux`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAnimaux(data.animaux || []);
        setStats({
          totalLapins: data.animaux?.filter(a => a.espece === 'lapin').length || 0,
          totalChevaux: data.animaux?.filter(a => a.espece === 'cheval').length || 0,
          gestations: data.animaux?.filter(a => a.statut_reproduction === 'gestante').length || 0,
          alertesSante: data.animaux?.filter(a => a.alerte_sante).length || 0
        });
      }
    } catch (error) {
      console.error('Erreur chargement données élevage:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les animaux
  const animauxFiltres = animaux.filter(animal => {
    const matchSearch = animal.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       animal.identification?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEspece = selectedEspece === 'all' || animal.espece === selectedEspece;
    return matchSearch && matchEspece;
  });

  return (
    <div className="space-y-6" data-testid="elevage-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Rabbit className="w-6 h-6 text-amber-500" />
            Module Élevage
          </h1>
          <p className="text-muted-foreground">
            Gestion complète de vos élevages de lapins et chevaux
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddAnimal(true)} data-testid="add-animal-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un animal
          </Button>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Rabbit className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalLapins}</p>
                <p className="text-xs text-muted-foreground">Lapins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Horse className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalChevaux}</p>
                <p className="text-xs text-muted-foreground">Chevaux</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                <Baby className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.gestations}</p>
                <p className="text-xs text-muted-foreground">Gestations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.alertesSante}</p>
                <p className="text-xs text-muted-foreground">Alertes santé</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Onglets principaux */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-6 gap-2 h-auto p-1">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            <span className="hidden md:inline">Tableau de bord</span>
          </TabsTrigger>
          <TabsTrigger value="animaux" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden md:inline">Mes animaux</span>
          </TabsTrigger>
          <TabsTrigger value="alimentation" className="flex items-center gap-2">
            <Utensils className="w-4 h-4" />
            <span className="hidden md:inline">Alimentation</span>
          </TabsTrigger>
          <TabsTrigger value="sante" className="flex items-center gap-2">
            <Stethoscope className="w-4 h-4" />
            <span className="hidden md:inline">Santé</span>
          </TabsTrigger>
          <TabsTrigger value="reproduction" className="flex items-center gap-2">
            <Heart className="w-4 h-4" />
            <span className="hidden md:inline">Reproduction</span>
          </TabsTrigger>
          <TabsTrigger value="reglementation" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            <span className="hidden md:inline">Réglementation</span>
          </TabsTrigger>
        </TabsList>

        {/* Contenu Tableau de bord */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Section Lapins */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Rabbit className="w-5 h-5 text-amber-500" />
                  Élevage Lapins
                </CardTitle>
                <CardDescription>
                  Vue d'ensemble de votre cuniculture
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Mâles reproducteurs</p>
                    <p className="text-xl font-bold">-</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Femelles reproductrices</p>
                    <p className="text-xl font-bold">-</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Lapereaux sevrés</p>
                    <p className="text-xl font-bold">-</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">En engraissement</p>
                    <p className="text-xl font-bold">-</p>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Prochaines échéances</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                      <span>Rappel vaccin Myxomatose</span>
                      <Badge variant="outline">3 animaux</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <span>Mises bas prévues</span>
                      <Badge variant="outline">-</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section Chevaux */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Horse className="w-5 h-5 text-blue-500" />
                  Élevage Chevaux
                </CardTitle>
                <CardDescription>
                  Vue d'ensemble de votre écurie
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Étalons</p>
                    <p className="text-xl font-bold">-</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Juments</p>
                    <p className="text-xl font-bold">-</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Poulains</p>
                    <p className="text-xl font-bold">-</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Hongres</p>
                    <p className="text-xl font-bold">-</p>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Prochaines échéances</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
                      <span>Rappel vaccin grippe</span>
                      <Badge variant="outline">-</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <span>Vermifugation prévue</span>
                      <Badge variant="outline">-</Badge>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <span>Visite maréchal</span>
                      <Badge variant="outline">-</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contenu Mes Animaux */}
        <TabsContent value="animaux" className="space-y-4">
          {/* Filtres */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou identification..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-animal"
              />
            </div>
            <Select value={selectedEspece} onValueChange={setSelectedEspece}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-espece">
                <SelectValue placeholder="Espèce" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les espèces</SelectItem>
                <SelectItem value="lapin">Lapins</SelectItem>
                <SelectItem value="cheval">Chevaux</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Liste des animaux */}
          {animauxFiltres.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Aucun animal enregistré</h3>
                <p className="text-muted-foreground mb-4">
                  Commencez par ajouter vos premiers animaux à votre élevage.
                </p>
                <Button onClick={() => setShowAddAnimal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un animal
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {animauxFiltres.map((animal) => (
                <Card key={animal.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${animal.espece === 'lapin' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                          {animal.espece === 'lapin' ? 
                            <Rabbit className="w-5 h-5 text-amber-600" /> : 
                            <Horse className="w-5 h-5 text-blue-600" />
                          }
                        </div>
                        <div>
                          <h4 className="font-medium">{animal.nom}</h4>
                          <p className="text-sm text-muted-foreground">{animal.identification}</p>
                        </div>
                      </div>
                      <Badge variant={animal.statut === 'actif' ? 'default' : 'secondary'}>
                        {animal.statut}
                      </Badge>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Race:</span>
                        <p className="font-medium">{animal.race || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sexe:</span>
                        <p className="font-medium">{animal.sexe || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Né(e) le:</span>
                        <p className="font-medium">{animal.date_naissance || '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Poids:</span>
                        <p className="font-medium">{animal.poids ? `${animal.poids} kg` : '-'}</p>
                      </div>
                    </div>
                    {animal.alerte_sante && (
                      <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {animal.alerte_sante}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Contenu Alimentation */}
        <TabsContent value="alimentation" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Alimentation Lapins */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Rabbit className="w-5 h-5 text-amber-500" />
                  Alimentation Lapins
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">BASE QUOTIDIENNE</h4>
                  <div className="space-y-2">
                    {ALIMENTATION_LAPIN.base.map((item, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg">
                        <div className="flex justify-between items-start">
                          <span className="font-medium">{item.aliment}</span>
                          {item.obligatoire && <Badge variant="destructive" className="text-xs">Obligatoire</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{item.quantite}</p>
                        <p className="text-xs text-blue-600 mt-1">{item.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">REPRODUCTION</h4>
                  <div className="space-y-2">
                    {ALIMENTATION_LAPIN.reproduction.map((item, idx) => (
                      <div key={idx} className="p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                        <span className="font-medium">{item.aliment}</span>
                        <p className="text-sm text-muted-foreground">{item.quantite} - {item.periode}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium text-sm text-red-600 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    ALIMENTS INTERDITS
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {ALIMENTATION_LAPIN.interdits.map((item, idx) => (
                      <Badge key={idx} variant="outline" className="text-red-600 border-red-200">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Alimentation Chevaux */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Horse className="w-5 h-5 text-blue-500" />
                  Alimentation Chevaux
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">BASE QUOTIDIENNE</h4>
                  <div className="space-y-2">
                    {ALIMENTATION_CHEVAL.base.map((item, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg">
                        <div className="flex justify-between items-start">
                          <span className="font-medium">{item.aliment}</span>
                          {item.obligatoire && <Badge variant="destructive" className="text-xs">Obligatoire</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{item.quantite}</p>
                        <p className="text-xs text-blue-600 mt-1">{item.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">SELON TRAVAIL</h4>
                  <div className="space-y-2">
                    {ALIMENTATION_CHEVAL.travail.map((item, idx) => (
                      <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <span className="font-medium">{item.aliment}</span>
                        <p className="text-sm text-muted-foreground">{item.quantite}</p>
                        <p className="text-xs text-blue-600">{item.type_travail}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">COMPLÉMENTS</h4>
                  <div className="space-y-2">
                    {ALIMENTATION_CHEVAL.complement.map((item, idx) => (
                      <div key={idx} className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <span className="font-medium">{item.nom}</span>
                        <p className="text-sm text-muted-foreground">{item.frequence} - {item.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium text-sm text-red-600 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    ALIMENTS INTERDITS/DANGEREUX
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {ALIMENTATION_CHEVAL.interdits.map((item, idx) => (
                      <Badge key={idx} variant="outline" className="text-red-600 border-red-200">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contenu Santé */}
        <TabsContent value="sante" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Santé Lapins */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Syringe className="w-5 h-5 text-amber-500" />
                  Protocole Sanitaire Lapins
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">VACCINATIONS</h4>
                  <div className="space-y-2">
                    {REGLEMENTATION_LAPIN.vaccinations.map((vaccin, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${vaccin.obligatoire ? 'bg-red-50 dark:bg-red-900/20 border border-red-200' : 'bg-muted'}`}>
                        <div className="flex justify-between items-start">
                          <span className="font-medium">{vaccin.nom}</span>
                          {vaccin.obligatoire && <Badge variant="destructive" className="text-xs">Obligatoire</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">Fréquence: {vaccin.frequence}</p>
                        <p className="text-xs text-blue-600 mt-1">{vaccin.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">CONDITIONS D'ÉLEVAGE</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Home className="w-4 h-4 text-muted-foreground" />
                      <span>{REGLEMENTATION_LAPIN.normes_bien_etre.surface_min_par_animal}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Thermometer className="w-4 h-4 text-muted-foreground" />
                      <span>{REGLEMENTATION_LAPIN.normes_bien_etre.temperature}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Sun className="w-4 h-4 text-muted-foreground" />
                      <span>{REGLEMENTATION_LAPIN.normes_bien_etre.eclairage}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Santé Chevaux */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Stethoscope className="w-5 h-5 text-blue-500" />
                  Protocole Sanitaire Chevaux
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">VACCINATIONS</h4>
                  <div className="space-y-2">
                    {REGLEMENTATION_CHEVAL.vaccinations.map((vaccin, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${vaccin.obligatoire ? 'bg-red-50 dark:bg-red-900/20 border border-red-200' : 'bg-muted'}`}>
                        <div className="flex justify-between items-start">
                          <span className="font-medium">{vaccin.nom}</span>
                          {vaccin.obligatoire && <Badge variant="destructive" className="text-xs">Obligatoire</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">Fréquence: {vaccin.frequence}</p>
                        <p className="text-xs text-blue-600 mt-1">{vaccin.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">VERMIFUGATION</h4>
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="font-medium">Fréquence: {REGLEMENTATION_CHEVAL.vermifugation.frequence}</p>
                    <p className="text-sm text-muted-foreground mt-1">Principes actifs recommandés:</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {REGLEMENTATION_CHEVAL.vermifugation.principes_actifs.map((pa, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{pa}</Badge>
                      ))}
                    </div>
                    <p className="text-xs text-amber-600 mt-2">💡 {REGLEMENTATION_CHEVAL.vermifugation.coproscopie}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">SOINS RÉGULIERS</h4>
                  <div className="space-y-2">
                    {REGLEMENTATION_CHEVAL.soins_obligatoires.map((soin, idx) => (
                      <div key={idx} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <span className="font-medium">{soin.nom}</span>
                        <p className="text-sm text-muted-foreground">{soin.frequence} - {soin.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bouton enregistrer intervention santé */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h4 className="font-medium">Enregistrer une intervention vétérinaire</h4>
                  <p className="text-sm text-muted-foreground">Vaccinations, traitements, consultations...</p>
                </div>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle intervention
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contenu Reproduction */}
        <TabsContent value="reproduction" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Reproduction Lapins */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Heart className="w-5 h-5 text-pink-500" />
                  Reproduction Lapins
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-muted-foreground">Maturité sexuelle</p>
                    <p className="font-medium">4-5 mois (femelle) / 5-6 mois (mâle)</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-muted-foreground">Gestation</p>
                    <p className="font-medium">31 jours (28-33)</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-muted-foreground">Portée moyenne</p>
                    <p className="font-medium">6-10 lapereaux</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-muted-foreground">Sevrage</p>
                    <p className="font-medium">4-5 semaines</p>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Cycle de reproduction</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J0</Badge>
                      <span>Saillie</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J12-14</Badge>
                      <span>Palpation (vérification gestation)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J25</Badge>
                      <span>Installation boîte à nid</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J31</Badge>
                      <span>Mise bas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J35</Badge>
                      <span>Sevrage possible</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reproduction Chevaux */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Baby className="w-5 h-5 text-blue-500" />
                  Reproduction Chevaux
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-muted-foreground">Maturité sexuelle</p>
                    <p className="font-medium">18 mois (mise à la repro: 3 ans)</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-muted-foreground">Gestation</p>
                    <p className="font-medium">11 mois (320-360 jours)</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-muted-foreground">Saison de monte</p>
                    <p className="font-medium">Février à Juillet</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-muted-foreground">Sevrage</p>
                    <p className="font-medium">6 mois</p>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Suivi de gestation</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J14-16</Badge>
                      <span>Échographie (confirmation gestation)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J25-30</Badge>
                      <span>Échographie (battement cardiaque)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J45</Badge>
                      <span>Échographie (vérification gémellité)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">5ème mois</Badge>
                      <span>Vaccin rhinopneumonie #1</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">7ème mois</Badge>
                      <span>Vaccin rhinopneumonie #2</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">9ème mois</Badge>
                      <span>Vaccin rhinopneumonie #3</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">J320+</Badge>
                      <span>Surveillance poulinage</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions reproduction */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                  <Heart className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="font-medium">Enregistrer une saillie</p>
                  <p className="text-sm text-muted-foreground">Lapin ou cheval</p>
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Baby className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Déclarer une naissance</p>
                  <p className="text-sm text-muted-foreground">Mise bas ou poulinage</p>
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Suivi échographies</p>
                  <p className="text-sm text-muted-foreground">Planifier et enregistrer</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contenu Réglementation */}
        <TabsContent value="reglementation" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Réglementation Lapins */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BookOpen className="w-5 h-5 text-amber-500" />
                  Réglementation Cuniculture
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
                  <h4 className="font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Identification
                  </h4>
                  <p className="text-sm mt-1">{REGLEMENTATION_LAPIN.identification.description}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Référence: {REGLEMENTATION_LAPIN.identification.texte_reference}
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Registres obligatoires</h4>
                  <div className="space-y-2">
                    {REGLEMENTATION_LAPIN.registre_obligatoire.map((registre, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded">
                        <ClipboardList className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{registre}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Normes bien-être animal</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Surface:</strong> {REGLEMENTATION_LAPIN.normes_bien_etre.surface_min_par_animal}</p>
                    <p><strong>Température:</strong> {REGLEMENTATION_LAPIN.normes_bien_etre.temperature}</p>
                    <p><strong>Éclairage:</strong> {REGLEMENTATION_LAPIN.normes_bien_etre.eclairage}</p>
                    <p><strong>Ventilation:</strong> {REGLEMENTATION_LAPIN.normes_bien_etre.ventilation}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Réglementation Chevaux */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                  Réglementation Équine
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                  <h4 className="font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Identification obligatoire
                  </h4>
                  <p className="text-sm mt-1">{REGLEMENTATION_CHEVAL.identification.description}</p>
                  <p className="text-xs text-amber-600 mt-2">
                    ⚠️ Délai: {REGLEMENTATION_CHEVAL.identification.delai}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Référence: {REGLEMENTATION_CHEVAL.identification.texte_reference}
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Normes d'hébergement</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Home className="w-4 h-4 text-muted-foreground" />
                      <span>Box: {REGLEMENTATION_CHEVAL.normes_bien_etre.surface_box}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <TreePine className="w-4 h-4 text-muted-foreground" />
                      <span>Paddock: {REGLEMENTATION_CHEVAL.normes_bien_etre.surface_paddock}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Sun className="w-4 h-4 text-muted-foreground" />
                      <span>Pré: {REGLEMENTATION_CHEVAL.normes_bien_etre.surface_pre}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Droplets className="w-4 h-4 text-muted-foreground" />
                      <span>Eau: {REGLEMENTATION_CHEVAL.normes_bien_etre.eau}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Documents obligatoires</h4>
                  <div className="space-y-2 text-sm">
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span>Livret signalétique (passeport équin)</span>
                    </div>
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span>Carte d'immatriculation SIRE</span>
                    </div>
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span>Registre des équidés (détenteur)</span>
                    </div>
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span>Carnet de vaccination</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Ajouter Animal */}
      <Dialog open={showAddAnimal} onOpenChange={setShowAddAnimal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter un animal</DialogTitle>
            <DialogDescription>
              Enregistrez un nouvel animal dans votre élevage
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Espèce *</Label>
                <Select>
                  <SelectTrigger data-testid="select-espece">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lapin">Lapin</SelectItem>
                    <SelectItem value="cheval">Cheval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sexe *</Label>
                <Select>
                  <SelectTrigger data-testid="select-sexe">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Mâle</SelectItem>
                    <SelectItem value="femelle">Femelle</SelectItem>
                    <SelectItem value="hongre">Hongre (castré)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input placeholder="Nom de l'animal" data-testid="input-nom-animal" />
            </div>
            <div className="space-y-2">
              <Label>Identification (tatouage/puce/SIRE)</Label>
              <Input placeholder="N° d'identification" data-testid="input-identification" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Race</Label>
                <Input placeholder="Race" data-testid="input-race" />
              </div>
              <div className="space-y-2">
                <Label>Date de naissance</Label>
                <Input type="date" data-testid="input-date-naissance" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea placeholder="Informations complémentaires..." data-testid="input-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAnimal(false)}>
              Annuler
            </Button>
            <Button onClick={() => {
              toast.success('Animal ajouté avec succès');
              setShowAddAnimal(false);
            }} data-testid="btn-save-animal">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
